import React, { useLayoutEffect, useRef } from "react";
import styled from "styled-components";
import { gsap } from "gsap";
import { theme } from "./theme";
import bannerImg from "../assets/bannerBg3.png";
import bannerImgMobile from "../assets/bannerBg3Mobile.png";

export default function Hero() {
  const containerRef = useRef(null);
  const eyebrowRef = useRef(null);
  const headlineRef = useRef(null);
  const subRef = useRef(null);
  const ctaRef = useRef(null);
  const scrollLineRef = useRef(null);
  const overlayRef = useRef(null);

  useLayoutEffect(() => {
    const ctx = gsap.context(() => {
      const tl = gsap.timeline({ defaults: { ease: "power3.out" } });

      tl.fromTo(
        overlayRef.current,
        { opacity: 0 },
        { opacity: 1, duration: 1.2 },
      )
        .fromTo(
          eyebrowRef.current,
          { opacity: 0, y: 20, letterSpacing: "0.4em" },
          { opacity: 1, y: 0, letterSpacing: "0.25em", duration: 1 },
          "-=0.6",
        )
        .fromTo(
          headlineRef.current.querySelectorAll(".word"),
          { opacity: 0, y: 60, rotateX: -30 },
          {
            opacity: 1,
            y: 0,
            rotateX: 0,
            duration: 1.1,
            stagger: 0.12,
          },
          "-=0.5",
        )
        .fromTo(
          subRef.current,
          { opacity: 0, y: 24 },
          { opacity: 1, y: 0, duration: 0.9 },
          "-=0.5",
        )
        .fromTo(
          ctaRef.current,
          { opacity: 0, y: 20 },
          { opacity: 1, y: 0, duration: 0.8 },
          "-=0.5",
        )
        .fromTo(
          scrollLineRef.current,
          { opacity: 0, scaleY: 0 },
          { opacity: 1, scaleY: 1, duration: 1, ease: "power2.out" },
          "-=0.3",
        );
    }, containerRef);

    return () => ctx.revert();
  }, []);

  const headline = ["Kujdesu për", "shëndetin tënd!"];

  return (
    <HeroWrapper ref={containerRef}>
      {/* CHANGED: Wrapped image in a HTML5 picture tag for intelligent responsive loading */}
      <ResponsivePictureWrapper>
        <source srcSet={bannerImgMobile} media="(max-width: 480px)" />
        <HeroBg src={bannerImg} alt="" aria-hidden="true" />
      </ResponsivePictureWrapper>

      <Overlay ref={overlayRef} />
      <GrainLayer />

      <Content>
        <Eyebrow ref={eyebrowRef}>OrgaLife — Detox Ginger Shots</Eyebrow>

        <Headline ref={headlineRef} aria-label="Kujdesu për shëndetin tënd!">
          {headline.map((word, i) => (
            <span className="word" key={i}>
              {word}
              {i < headline.length - 1 ? "\u00A0" : ""}
            </span>
          ))}
        </Headline>

        <Sub ref={subRef}>
          Shots organik për energji &amp; mirëqenie — 100% natyral
        </Sub>

        <CtaRow ref={ctaRef}>
          <PrimaryBtn onClick={() => document.getElementById("products")?.scrollIntoView({ behavior: "smooth" })}>Shop Now</PrimaryBtn>
          <GhostBtn>Learn More ↓</GhostBtn>
        </CtaRow>
      </Content>

      <ScrollIndicator ref={scrollLineRef}>
        <ScrollLine />
        <ScrollLabel>Scroll</ScrollLabel>
      </ScrollIndicator>
      <BottomGradient />

      <BadgeWrap>
        <Badge>🌿 Organic</Badge>
        <Badge>🔥 Raw</Badge>
        <Badge>✦ Vegan</Badge>
      </BadgeWrap>
    </HeroWrapper>
  );
}

/* ─── Styled Components ─────────────────────────────── */
const BottomGradient = styled.div`
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  height: 30%;
  z-index: 3;
  background: linear-gradient(to top, #6f7c40, transparent);
  pointer-events: none;
`;

const HeroWrapper = styled.section`
  position: relative;
  width: 100%;
  height: 100vh;
  min-height: 600px;
  display: flex;
  justify-content: center;
  overflow: hidden;

  /* Desktop */
  padding-top: 10em;

  /* Tablet */
  @media (max-width: 768px) {
    padding-top: 6em;
    align-items: flex-start;
    min-height: 100svh;
  }

  /* Mobile */
  @media (max-width: 480px) {
    padding-top: 35%;
    align-items: flex-start;
  }
`;

/* ADDED: Picture element structural block to match absolute container dimensions */
const ResponsivePictureWrapper = styled.picture`
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
`;

const HeroBg = styled.img`
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  object-fit: cover;
  object-position: center;
  transform: scale(1.04);
  will-change: transform;
`;

const Overlay = styled.div`
  position: absolute;
  inset: 0;
  z-index: 1;
`;

const GrainLayer = styled.div`
  position: absolute;
  inset: 0;
  z-index: 2;
  background-repeat: repeat;
  background-size: 200px 200px;
  pointer-events: none;
`;

const Content = styled.div`
  position: relative;
  z-index: 10;
  text-align: center;
  padding: 0 1.5rem;
  max-width: 900px;
  perspective: 1000px;
  width: 100%;

  @media (max-width: 768px) {
    padding: 0 1.25rem;
    max-width: 100%;
  }

  @media (max-width: 480px) {
    padding: 0 1.25rem;
  }
`;

const Eyebrow = styled.p`
  font-family: ${theme.fonts.mono};
  font-size: clamp(0.6rem, 2.5vw, 0.8rem);
  letter-spacing: 0.25em;
  text-transform: uppercase;
  color: #d3cfcf;
  margin-bottom: 1.25rem;
  opacity: 0;

  @media (max-width: 480px) {
    letter-spacing: 0.12em;
    margin-bottom: 1rem;
    font-size: 0.65rem;
  }
`;

const Headline = styled.h1`
  font-size: clamp(2rem, 7vw, 5vw);
  font-weight: 500;
  line-height: 1.1;
  color: white;
  letter-spacing: -0.02em;
  margin-bottom: 0.8rem;
  font-family: "Montserrat", sans-serif;
  font-style: italic;

  .word {
    display: inline-block;
    opacity: 0;
    transform-origin: top center;
  }

  .word:last-child {
    font-family: "Playfair Display";
    color: yellow;
    font-style: italic;
  }

  /* Tablet */
  @media (max-width: 768px) {
    font-size: clamp(2.2rem, 8vw, 3.8rem);
    line-height: 1.15;
  }

  /* Mobile: stack words, big and readable */
  @media (max-width: 480px) {
    font-size: clamp(2.4rem, 11vw, 3.2rem);
    line-height: 1.2;
    letter-spacing: -0.01em;
    margin-bottom: 1rem;

    .word {
      display: block;
    }
  }
`;

const Sub = styled.p`
  font-size: clamp(0.85rem, 2.2vw, 1.15rem);
  color: #dad5d5;
  font-weight: 300;
  line-height: 1.8;
  margin-bottom: 1rem;
  opacity: 0;

  @media (max-width: 768px) {
    font-size: clamp(0.9rem, 2.5vw, 1.05rem);
  }

  @media (max-width: 480px) {
    font-size: 0.75rem;
    line-height: 1.65;
    margin-bottom: 1.25rem;
  }
`;

const CtaRow = styled.div`
  display: flex;
  gap: 1rem;
  justify-content: center;
  align-items: center;
  flex-wrap: wrap;
  opacity: 0;
  margin-bottom: 10em;

  @media (max-width: 768px) {
    margin-bottom: 4em;
    gap: 0.75rem;
  }

  @media (max-width: 480px) {
    margin-bottom: 2em;
    gap: 0.65rem;
    width: 100%;
  }
`;

const PrimaryBtn = styled.button`
  font-family: ${theme.fonts.body};
  font-size: 0.875rem;
  font-weight: 500;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  padding: 1rem 2.5rem;
  background: #89d146;
  color: white;
  border-radius: ${theme.radius.pill};
  transition: all ${theme.transitions.base};
  position: relative;
  overflow: hidden;
  outline: none;
  border: none;
  cursor: pointer;

  &::before {
    content: "";
    position: absolute;
    inset: 0;
    transform: translateX(-101%);
    transition: transform 0.4s cubic-bezier(0.16, 1, 0.3, 1);
  }

  &:hover::before {
    transform: translateX(0);
  }

  span {
    position: relative;
    z-index: 1;
  }

  /* Full-width tap target on mobile */
  @media (max-width: 480px) {
    width: 40%;
    padding: 0.95rem 2rem;
    font-size: 0.75rem;
    min-height: 48px;
  }
`;

const GhostBtn = styled.button`
  font-family: "Montserrat", sans-serif;
  font-size: 0.875rem;
  font-weight: 400;
  letter-spacing: 0.06em;
  padding: 1rem 2rem;
  color: #486d18;
  border-radius: ${theme.radius.pill};
  transition: all ${theme.transitions.base};
  border: none;
  outline: none;
  cursor: pointer;
  background: white;

  @media (max-width: 480px) {
    width: 40%;
    font-size: 0.75rem;
    min-height: 48px;
    border: 1px solid rgba(72, 109, 24, 0.35);
    padding: 0.9rem 1rem;
  }
`;

const ScrollIndicator = styled.div`
  position: absolute;
  bottom: 2.5rem;
  left: 50%;
  transform: translateX(-50%);
  z-index: 10;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.5rem;
  opacity: 0;
  transform-origin: top center;

  /* Hide on very short screens */
  @media (max-height: 600px) {
    display: none;
  }

  @media (max-width: 480px) {
    bottom: 1.5rem;
  }
`;

const ScrollLine = styled.div`
  width: 1px;
  height: 60px;
  background: linear-gradient(to bottom, ${theme.colors.accent}, transparent);
  animation: pulse-line 2s ease-in-out infinite;

  @keyframes pulse-line {
    0%,
    100% {
      opacity: 0.4;
      transform: scaleY(1);
    }
    50% {
      opacity: 1;
      transform: scaleY(1.15);
    }
  }

  @media (max-width: 480px) {
    height: 40px;
  }
`;

const ScrollLabel = styled.span`
  font-family: ${theme.fonts.mono};
  font-size: 0.6rem;
  letter-spacing: 0.2em;
  text-transform: uppercase;
  color: ${theme.colors.textMuted};
  writing-mode: horizontal-tb;
`;

const BadgeWrap = styled.div`
  position: absolute;
  bottom: 2.5rem;
  right: 2.5rem;
  z-index: 10;
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  align-items: flex-end;

  /* Hidden on mobile — reclaim screen space */
  @media (max-width: 600px) {
    display: none;
  }

  /* Pull in closer on tablet */
  @media (max-width: 768px) {
    right: 1.25rem;
    bottom: 1.5rem;
    gap: 0.35rem;
  }
`;

const Badge = styled.span`
  font-family: ${theme.fonts.mono};
  font-size: 0.65rem;
  letter-spacing: 0.12em;
  text-transform: uppercase;
  color: ${theme.colors.textSecondary};
  background: ${theme.colors.bgCard};
  border: 1px solid ${theme.colors.borderSubtle};
  padding: 0.35rem 0.75rem;
  border-radius: ${theme.radius.pill};

  @media (max-width: 768px) {
    font-size: 0.6rem;
    padding: 0.3rem 0.6rem;
  }
`;
